/**
 * Program Benefits Service
 * Benefits tracking and milestones
 */
import { apiClient } from './api'

export interface ProgramBenefit {
  id: number
  program_id: number
  category: string
  benefit_name: string
  target_value: number
  realized_value: number
  projected_value: number
  unit: string
  measurement_method?: string
  owner_id?: number
  status: string
  metadata?: Record<string, unknown>
}

export interface BenefitMilestone {
  id: number
  program_id: number
  benefit_id?: number
  milestone_name: string
  target_date: string
  expected_value: number
  realized_value?: number
  status: string
  achieved_date?: string
  metadata?: Record<string, unknown>
}

export interface BenefitsSummary {
  total_benefits: number
  total_target_value: number
  total_realized_value: number
  total_projected_value: number
  achievement_rate: number
  by_category: Record<string, {
    count: number
    target_value: number
    realized_value: number
  }>
}

export const programBenefitsService = {
  /**
   * List benefits
   */
  async listBenefits(programId: number): Promise<ProgramBenefit[]> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/benefits`
    )
    return response.data
  },

  /**
   * Create benefit
   */
  async createBenefit(programId: number, data: Partial<ProgramBenefit>): Promise<ProgramBenefit> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/benefits`,
      data
    )
    return response.data
  },

  /**
   * Update benefit
   */
  async updateBenefit(
    programId: number,
    benefitId: number,
    data: Partial<ProgramBenefit>
  ): Promise<ProgramBenefit> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/benefits/${benefitId}`,
      data
    )
    return response.data
  },

  /**
   * Delete benefit
   */
  async deleteBenefit(programId: number, benefitId: number): Promise<void> {
    await apiClient.delete(
      `/api/v1/programs/${programId}/benefits/${benefitId}`
    )
  },

  /**
   * Get benefits summary
   */
  async getSummary(programId: number): Promise<BenefitsSummary> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/benefits/summary`
    )
    return response.data
  },

  /**
   * List benefit milestones
   */
  async listMilestones(programId: number, benefitId?: number): Promise<BenefitMilestone[]> {
    const params: Record<string, unknown> = {}
    if (benefitId) params.benefit_id = benefitId
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/benefits/milestones`,
      { params }
    )
    return response.data
  },

  /**
   * Create benefit milestone
   */
  async createMilestone(programId: number, data: Partial<BenefitMilestone>): Promise<BenefitMilestone> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/benefits/milestones`,
      data
    )
    return response.data
  },

  /**
   * Update benefit milestone
   */
  async updateMilestone(
    programId: number,
    milestoneId: number,
    data: Partial<BenefitMilestone>
  ): Promise<BenefitMilestone> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/benefits/milestones/${milestoneId}`,
      data
    )
    return response.data
  },

  /**
   * Delete benefit milestone
   */
  async deleteMilestone(programId: number, milestoneId: number): Promise<void> {
    await apiClient.delete(
      `/api/v1/programs/${programId}/benefits/milestones/${milestoneId}`
    )
  },
}

